import React from 'react';
import { createLogo, createUPIQR, createSealOrSign } from '../../utils/imageUtils';
import { roundTo2Decimals, roundToNearestInteger, calculateGSTAmount } from '../../utils/numberFormat';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';
import { hasSubPartyBreakdown } from './shared/subPartyUtils';
import { calculateInvoiceDiscount } from './shared/billFormatUtils';

const PAGE_DIMENSIONS = {
  a4: { portrait: { width: 210, height: 297 }, landscape: { width: 297, height: 210 } },
  a5: { portrait: { width: 148, height: 210 }, landscape: { width: 210, height: 148 } },
  letter: { portrait: { width: 216, height: 279 }, landscape: { width: 279, height: 216 } },
};

/** Shared compact invoice layout for browser print and html2pdf (screen-only export class). */
function buildInvoiceCompactLayoutCss(rootSel, pageDims) {
  const w = pageDims.width;
  const r = rootSel;
  return `
    ${r} {
      width: ${w}mm !important;
      min-width: ${w}mm !important;
      max-width: ${w}mm !important;
      min-height: auto !important;
      height: auto !important;
      padding: 8mm !important;
      box-sizing: border-box !important;
      overflow: visible !important;
      overflow-x: visible !important;
      overflow-y: visible !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
    }
    ${r}.invoice-screen-preview-boost,
    ${r} .invoice-screen-preview-boost {
      font-size: 100% !important;
    }
    ${r} .invoice-print-inner {
      transform: none !important;
      width: 100% !important;
      max-width: 100% !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      overflow: visible !important;
    }
    ${r} .invoice-filler-row { display: none !important; }
    ${r} .invoice-doc-header {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    ${r} .invoice-table-wrap {
      break-inside: auto !important;
      page-break-inside: auto !important;
      overflow: visible !important;
      max-height: none !important;
    }
    ${r} .invoice-post-items {
      /* Allow split across pages — avoid clips bank/totals/footer when block is tall */
      break-inside: auto !important;
      page-break-inside: auto !important;
      overflow: visible !important;
    }
    ${r} table {
      table-layout: fixed !important;
      width: 100% !important;
      max-width: 100% !important;
      page-break-inside: auto !important;
    }
    ${r} thead { display: table-header-group !important; }
    ${r} tfoot { display: table-footer-group !important; }
    ${r} tr {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    ${r} th, ${r} td {
      padding: 4px !important;
      font-size: 9px !important;
      word-wrap: break-word !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
      overflow: visible !important;
      max-width: 100% !important;
    }
    ${r} .invoice-items-table th,
    ${r} .invoice-items-table td {
      vertical-align: middle !important;
      padding: 6px 8px !important;
      font-size: 9px !important;
    }
    ${r} .invoice-items-table--with-subparty th:nth-child(1),
    ${r} .invoice-items-table--with-subparty td:nth-child(1) { text-align: center !important; }
    ${r} .invoice-items-table--with-subparty th:nth-child(2),
    ${r} .invoice-items-table--with-subparty td:nth-child(2) { text-align: left !important; }
    ${r} .invoice-items-table--with-subparty th:nth-child(3),
    ${r} .invoice-items-table--with-subparty td:nth-child(3) {
      text-align: justify !important;
      text-justify: inter-word !important;
    }
    ${r} .invoice-items-table--with-subparty td:nth-child(4),
    ${r} .invoice-items-table--with-subparty th:nth-child(4) { text-align: center !important; }
    ${r} .invoice-items-table--with-subparty td:nth-child(5),
    ${r} .invoice-items-table--with-subparty th:nth-child(5) { text-align: center !important; }
    ${r} .invoice-items-table--with-subparty td:nth-child(6),
    ${r} .invoice-items-table--with-subparty th:nth-child(6) { text-align: right !important; }
    ${r} .invoice-items-table--with-subparty td:nth-child(7),
    ${r} .invoice-items-table--with-subparty th:nth-child(7) { text-align: right !important; }
    ${r} .invoice-items-table--with-subparty td:nth-child(8),
    ${r} .invoice-items-table--with-subparty th:nth-child(8) { text-align: right !important; }
    ${r} .invoice-items-table--with-subparty td:nth-child(9),
    ${r} .invoice-items-table--with-subparty th:nth-child(9) { text-align: center !important; }
    ${r} .invoice-items-table--with-subparty td:nth-child(10),
    ${r} .invoice-items-table--with-subparty th:nth-child(10) { text-align: right !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(1), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(1) { width: 3% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(2), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(2) { width: 11% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(3), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(3) { width: 20% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(4), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(4) { width: 7% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(5), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(5) { width: 9% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(6), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(6) { width: 11% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(7), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(7) { width: 8% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(8), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(8) { width: 14% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(9), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(9) { width: 6% !important; }
    ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) th:nth-child(10), ${r} .invoice-items-table--with-subparty:not(.invoice-items-table--nogst) td:nth-child(10) { width: 11% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(1), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(1) { width: 4% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(2), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(2) { width: 12% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(3), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(3) { width: 26% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(4), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(4) { width: 11% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(5), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(5) { width: 12% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(6), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(6) { width: 10% !important; }
    ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst th:nth-child(7), ${r} .invoice-items-table--with-subparty.invoice-items-table--nogst td:nth-child(7) { width: 25% !important; }
    ${r} .invoice-items-table--no-subparty th:nth-child(1),
    ${r} .invoice-items-table--no-subparty td:nth-child(1) { text-align: center !important; }
    ${r} .invoice-items-table--no-subparty th:nth-child(2),
    ${r} .invoice-items-table--no-subparty td:nth-child(2) {
      text-align: justify !important;
      text-justify: inter-word !important;
    }
    ${r} .invoice-items-table--no-subparty td:nth-child(3),
    ${r} .invoice-items-table--no-subparty th:nth-child(3) { text-align: center !important; }
    ${r} .invoice-items-table--no-subparty td:nth-child(4),
    ${r} .invoice-items-table--no-subparty th:nth-child(4) { text-align: center !important; }
    ${r} .invoice-items-table--no-subparty td:nth-child(5),
    ${r} .invoice-items-table--no-subparty th:nth-child(5) { text-align: right !important; }
    ${r} .invoice-items-table--no-subparty td:nth-child(6),
    ${r} .invoice-items-table--no-subparty th:nth-child(6) { text-align: right !important; }
    ${r} .invoice-items-table--no-subparty td:nth-child(7),
    ${r} .invoice-items-table--no-subparty th:nth-child(7) { text-align: right !important; }
    ${r} .invoice-items-table--no-subparty td:nth-child(8),
    ${r} .invoice-items-table--no-subparty th:nth-child(8) { text-align: center !important; }
    ${r} .invoice-items-table--no-subparty td:nth-child(9),
    ${r} .invoice-items-table--no-subparty th:nth-child(9) { text-align: right !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(1), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(1) { width: 4% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(2), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(2) { width: 30% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(3), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(3) { width: 7% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(4), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(4) { width: 9% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(5), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(5) { width: 11% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(6), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(6) { width: 8% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(7), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(7) { width: 14% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(8), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(8) { width: 6% !important; }
    ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) th:nth-child(9), ${r} .invoice-items-table--no-subparty:not(.invoice-items-table--nogst) td:nth-child(9) { width: 11% !important; }
    ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst th:nth-child(1), ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst td:nth-child(1) { width: 5% !important; }
    ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst th:nth-child(2), ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst td:nth-child(2) { width: 38% !important; }
    ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst th:nth-child(3), ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst td:nth-child(3) { width: 11% !important; }
    ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst th:nth-child(4), ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst td:nth-child(4) { width: 12% !important; }
    ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst th:nth-child(5), ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst td:nth-child(5) { width: 10% !important; }
    ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst th:nth-child(6), ${r} .invoice-items-table--no-subparty.invoice-items-table--nogst td:nth-child(6) { width: 24% !important; }
    ${r} .seller-buyer-container {
      display: flex !important;
      flex-direction: row !important;
      gap: 12px !important;
    }
    ${r} .seller-buyer-box {
      border: 1px solid #d1d5db !important;
      border-radius: 6px !important;
      padding: 12px !important;
      background-color: #f9fafb !important;
    }
    ${r} .bank-totals-flex-row {
      display: flex !important;
      flex-direction: row !important;
      gap: 12px !important;
    }
    ${r} .bank-box {
      max-width: 46% !important;
      width: 100% !important;
      min-width: 0 !important;
      overflow-wrap: anywhere !important;
    }
    ${r} .totals-box {
      max-width: 54% !important;
      width: 100% !important;
      min-width: 0 !important;
      overflow-wrap: anywhere !important;
    }
    ${r} .totals-box table th,
    ${r} .totals-box table td {
      font-size: 14px !important;
      padding: 7px 9px !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
    }
    ${r} .invoice-footer {
      margin-top: 24px !important;
      padding-top: 36px !important;
      min-height: 88px !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    ${r} img, ${r} .print\\:visible {
      display: block !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      visibility: visible !important;
      max-width: 100% !important;
      height: auto !important;
      object-fit: contain !important;
      background: none !important;
      border: none !important;
    }
    ${r} img[alt*="Seal"], ${r} img[alt*="seal"] {
      max-width: 0.75in !important;
      max-height: 0.75in !important;
      width: 0.75in !important;
      height: 0.75in !important;
    }
    ${r} img[alt*="Sign"], ${r} img[alt*="sign"] {
      max-width: 1.5in !important;
      max-height: 1.5in !important;
      width: 1.5in !important;
      height: auto !important;
    }
  `;
}

function blur(val, previewMode) {
  if (!previewMode) return val;
  if (typeof val === 'number') return 'XXX';
  if (typeof val === 'string') return val.replace(/[^\s]/g, 'X');
  return 'XXX';
}

// Utility to format numbers with commas as per Indian system
function formatINR(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-IN');
}

// Helper for Indian number to words (no 'Rupees Only')
function numberToWordsIndian(num) {
  const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
  const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
  let str = '';
  if (num >= 10000000) {
    str += numberToWordsIndian(Math.floor(num / 10000000)) + ' Crore ';
    num = num % 10000000;
  }
  if (num >= 100000) {
    str += numberToWordsIndian(Math.floor(num / 100000)) + ' Lakh ';
    num = num % 100000;
  }
  if (num >= 1000) {
    str += numberToWordsIndian(Math.floor(num / 1000)) + ' Thousand ';
    num = num % 1000;
  }
  if (num >= 100) {
    str += numberToWordsIndian(Math.floor(num / 100)) + ' Hundred ';
    num = num % 100;
  }
  if (num > 0) {
    if (str !== '') str += 'and ';
    if (num < 20) str += a[num] + ' ';
    else str += b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '') + ' ';
  }
  return str.replace(/ +/g, ' ').trim();
}

function numToWords(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  if (num === 0) return 'Zero Rupees Only';
  return numberToWordsIndian(num) + ' Rupees Only';
}

function InvoiceTemplate({
  billData = {},
  companyDetails = {},
  partyDetails = {},
  bankDetails = {},
  pageSize = 'a4',
  orientation = 'portrait',
  previewMode = false,
  /** Larger on-screen layout, no filler rows visible (print/PDF still use full page + fillers) — use with previewMode=false for real invoices to share screenshots */
  sharePreview = false,
  showPlaceholders = false, // Show placeholders for missing data in template preview
}) {
  const subPartyBreakdownList = Array.isArray(billData.subPartyBreakdown) ? billData.subPartyBreakdown : [];

  const getSubPartyNameForBillRowIndex = (rowIndex) => {
    if (rowIndex == null || rowIndex < 0) return '';
    for (let i = 0; i < subPartyBreakdownList.length; i++) {
      const sp = subPartyBreakdownList[i];
      if (!sp || !Array.isArray(sp.lineIndices)) continue;
      if (sp.lineIndices.includes(rowIndex)) return String(sp.name || '').trim();
    }
    return '';
  };

  const SORT_UNASSIGNED = '\uFFFE';
  const rawItems = billData.items || [];
  const filteredIndexed = rawItems
    .map((it, billRowIndex) => ({ item: it, billRowIndex }))
    .filter(({ item: it }) => {
      const hasDesc = !!(it.description && String(it.description).trim());
      const hasItem = !!(it.item && String(it.item).trim());
      const hasNumbers =
        (parseFloat(it.qty) || 0) > 0 ||
        (parseFloat(it.rate) || 0) > 0 ||
        (parseFloat(it.amount) || 0) > 0 ||
        (parseFloat(it.total) || 0) > 0;
      return hasDesc || hasItem || hasNumbers;
    });

  // Sort by sub party name, then original line order; lines without a sub party sort last
  const visibleItems = [...filteredIndexed]
    .sort((a, b) => {
      const na = getSubPartyNameForBillRowIndex(a.billRowIndex) || SORT_UNASSIGNED;
      const nb = getSubPartyNameForBillRowIndex(b.billRowIndex) || SORT_UNASSIGNED;
      const c = na.localeCompare(nb, undefined, { sensitivity: 'base' });
      if (c !== 0) return c;
      return a.billRowIndex - b.billRowIndex;
    })
    .map(({ item, billRowIndex }) => ({
      ...item,
      _billRowIndex: billRowIndex,
      _subPartyName: getSubPartyNameForBillRowIndex(billRowIndex),
    }));

  const {
    discount,
    discountedSubtotal,
    discountRatio,
    discountPercentLabel,
    grossAmount,
  } = calculateInvoiceDiscount(visibleItems, billData);
  const totalSGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.sgst);
  }, 0) || 0;
  const totalCGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.cgst);
  }, 0) || 0;
  const totalIGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.igst || item.gst);
  }, 0) || 0;
  const totalVisibleServiceCharges = visibleItems.reduce((sum, item) => {
    const services = Array.isArray(item?.services) ? item.services : [];
    const rowServiceCharge = services.reduce((svcSum, service) => {
      if (!service?.showOnInvoice) return svcSum;
      const charge = parseFloat(service.customerCharge);
      return svcSum + (Number.isFinite(charge) ? charge : 0);
    }, 0);
    return sum + rowServiceCharge;
  }, 0) || 0;
  const grandTotalBeforeRoundOff = roundTo2Decimals(
    discountedSubtotal + totalSGST + totalCGST + totalIGST + totalVisibleServiceCharges
  );
  const grandTotal = roundToNearestInteger(grandTotalBeforeRoundOff);
  const nonGstTotalBeforeRoundOff = roundTo2Decimals(discountedSubtotal + totalVisibleServiceCharges);
  const nonGstTotal = roundToNearestInteger(nonGstTotalBeforeRoundOff);

  // GST summary table logic
  const gstSummary = {};
  visibleItems.forEach(item => {
    const gstPercent = (parseFloat(item.sgst || 0) + parseFloat(item.cgst || 0) + parseFloat(item.igst || 0)) || parseFloat(item.gst) || 0;
    const key = gstPercent.toString();
    if (!gstSummary[key]) {
      gstSummary[key] = {
        percent: gstPercent,
        taxable: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        total: 0,
      };
    }
    const amount = parseFloat(item.amount) || 0;
    const sgstAmt = calculateGSTAmount(amount, item.sgst);
    const cgstAmt = calculateGSTAmount(amount, item.cgst);
    const igstAmt = calculateGSTAmount(amount, item.igst);
    gstSummary[key].taxable += amount;
    gstSummary[key].sgst += sgstAmt;
    gstSummary[key].cgst += cgstAmt;
    gstSummary[key].igst += igstAmt;
    gstSummary[key].total += sgstAmt + cgstAmt + igstAmt;
  });
  const gstTableRows = Object.values(gstSummary).sort((a, b) => a.percent - b.percent);

  const screenPreviewBoost = previewMode || sharePreview;

  // Get page dimensions
  const pageDims = PAGE_DIMENSIONS[pageSize]?.[orientation] || PAGE_DIMENSIONS.a4.portrait;
  const pageStyle = {
    width: '100%',
    maxWidth: `${pageDims.width}mm`,
    minHeight: screenPreviewBoost || previewMode ? 'auto' : `${pageDims.height}mm`,
    margin: '0 auto',
    background: 'white',
    boxSizing: 'border-box',
    padding: '14px',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'visible',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    borderRadius: '6px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    display: 'block',
  };

  const minVisibleRows = 10;
  const emptyRowCount = Math.max(0, minVisibleRows - visibleItems.length);
  const itemRowCellPadding = '8px 6px';
  const numericCellPadding = '9px 10px';
  const isRegularGst = companyDetails.gstinType === 'Regular';
  const hasSubPartyColumn = hasSubPartyBreakdown(billData);
  const invoiceNote = String(billData.notes || '').trim();

  return (
    <div
      style={pageStyle}
      className={`print:p-0 print:shadow-none print:border-0 relative${screenPreviewBoost ? ' invoice-screen-preview-boost' : ''}`}
      id="invoice-template"
    >
      <div className="invoice-print-inner">
      <div className="invoice-doc-header">
      {/* Header */}
      <div className="invoice-header-row flex justify-between items-start mb-3 gap-2" style={{ minWidth: 0 }}>
        <div style={{ minWidth: 0, flex: '1 1 auto', overflow: 'visible' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {companyDetails.gstinType === 'Regular' && 'TAX INVOICE'}
            {companyDetails.gstinType === 'Composition' && 'BILL OF SUPPLY'}
            {companyDetails.gstinType === 'Unregistered' && 'INVOICE'}
          </h2>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            Invoice Number: {previewMode ? 'XXX-XXX/XXXX' : billData.invoiceNumber} | 
            Date: {previewMode ? 'YYYY-MM-DD' : formatDateToCompanyFormat(billData.date, companyDetails)}
          </div>
        </div>
        <div className="flex items-center flex-shrink-0">
          {createLogo(
            companyDetails.logoUrl,
            companyDetails.firmName || companyDetails.name || 'Company',
            screenPreviewBoost ? 'h-24 w-24' : 'h-16 w-16'
          )}
        </div>
      </div>

      {/* Billed By / Billed To */}
      <div className="flex flex-row gap-3 mb-3 seller-buyer-container">
        <div className="seller-buyer-box flex-1">
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px' }}>FROM</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px' }}>
            {companyDetails.name || companyDetails.firmName || (showPlaceholders ? '[Company Name - Add in Company Details]' : '')}
          </div>
          {(companyDetails.address || showPlaceholders) && (
            <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
              {companyDetails.address || (showPlaceholders ? '[Company Address - Add in Company Details]' : '')}
            </div>
          )}
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
            GST: {companyDetails.gstin || companyDetails.gstNumber || (showPlaceholders ? '[GSTIN - Add in Company Details]' : '-')} | 
            Mob: {companyDetails.contact || companyDetails.contactNumber || (showPlaceholders ? '[Contact Number - Add in Company Details]' : '-')}
          </div>
        </div>
        <div className="seller-buyer-box flex-1">
          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b', marginBottom: '4px' }}>TO</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b', marginBottom: '3px' }}>
            {previewMode ? 'XXX Party Name' : partyDetails.name}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>
            {previewMode ? 'XXX Address' : partyDetails.address}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
            GST: {previewMode ? 'XXGSTINXXXX' : (partyDetails.gstin || partyDetails.gstNumber || '-')} | 
            Mob: {previewMode ? 'XXXXXXXXXX' : (partyDetails.contact || '-')}
          </div>
        </div>
      </div>

      {Array.isArray(billData.subPartyBreakdown) && billData.subPartyBreakdown.length > 0 && (() => {
        const breakdown = billData.subPartyBreakdown;
        const itemsSrc = billData.items || [];
        const rowsSrc = billData.rows || [];
        const formatAssignedLines = (lineIndices) => {
          if (!Array.isArray(lineIndices) || lineIndices.length === 0) return '—';
          return lineIndices
            .map((li) => {
              const it = itemsSrc[li];
              const raw = rowsSrc[li] || {};
              const desc = (it && String(it.description || '').trim()) ? it.description : `Line ${li + 1}`;
              let m = '';
              if (raw.qtyDisplay) m = String(raw.qtyDisplay);
              else if (raw.qtyExpr && String(raw.qtyExpr).trim()) m = String(raw.qtyExpr).trim();
              const q = parseFloat(raw.qty) || 0;
              const u = raw.unit || raw.quantityMeasurement || '';
              if (!m && q > 0) m = `${q}${u ? ` ${u}` : ''}`;
              return m ? `${desc} (${m})` : desc;
            })
            .join('; ');
        };
        const breakdownSum = roundTo2Decimals(
          breakdown.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
        );
        const refTotal = parseFloat(billData.amount ?? billData.totalAmount);
        const compareTotal =
          Number.isFinite(refTotal) && refTotal > 0 ? refTotal : grandTotal;
        const breakdownOk = Math.abs(breakdownSum - compareTotal) < 0.02;
        return (
          <div
            className="mb-3"
            style={{
              fontSize: '10px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              padding: '8px 10px',
              background: '#f8fafc',
            }}
          >
            <div style={{ fontWeight: 'bold', color: '#475569', marginBottom: '6px' }}>
              Sub party breakdown (reference)
            </div>
            <table
              className="subparty-breakdown-table"
              style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
            >
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '58%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr style={{ color: '#64748b' }}>
                  <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>Sub party</th>
                  <th
                    className="subparty-breakdown-assigned"
                    style={{
                      textAlign: 'left',
                      padding: '4px 6px',
                      fontWeight: 600,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    Assigned lines
                  </th>
                  <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '4px 6px', color: '#1e293b', verticalAlign: 'top' }}>{row.name || '—'}</td>
                    <td
                      className="subparty-breakdown-assigned"
                      style={{
                        padding: '4px 6px',
                        color: '#475569',
                        verticalAlign: 'top',
                        lineHeight: 1.35,
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        hyphens: 'auto',
                      }}
                    >
                      {formatAssignedLines(row.lineIndices)}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', verticalAlign: 'top' }}>
                      ₹{roundTo2Decimals(parseFloat(row.amount) || 0)}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: '1px solid #cbd5e1', fontWeight: 600 }}>
                  <td style={{ padding: '6px', color: '#1e293b' }} colSpan={2}>
                    Total
                  </td>
                  <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>₹{breakdownSum}</td>
                </tr>
              </tbody>
            </table>
            <div
              style={{
                marginTop: '6px',
                fontSize: '9px',
                color: breakdownOk ? '#15803d' : '#b45309',
              }}
            >
              {breakdownOk
                ? `Matches invoice total (${previewMode ? '—' : `₹${compareTotal}`}).`
                : `Sum (₹${breakdownSum}) does not match invoice total (₹${compareTotal}).`}
            </div>
          </div>
        );
      })()}
      </div>

      {/* Item Table — rows may flow across pages; thead repeats on each printed page */}
      <div className="invoice-table-wrap" style={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table
        className={`w-full border mb-3 invoice-items-table${isRegularGst ? '' : ' invoice-items-table--nogst'}${hasSubPartyColumn ? ' invoice-items-table--with-subparty' : ' invoice-items-table--no-subparty'}`}
        style={{ 
        fontSize: '10px', 
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        width: '100%',
        maxWidth: '100%'
      }}>
        <thead>
          <tr style={{ background: '#667eea', color: 'white' }}>
            <th style={{ padding: '7px 5px', textAlign: 'center', verticalAlign: 'middle', borderRadius: '6px 0 0 0', width: isRegularGst ? '3%' : '4%' }}>#</th>
            {hasSubPartyColumn && (
              <th style={{ padding: '7px 5px', textAlign: 'left', verticalAlign: 'middle', width: isRegularGst ? '11%' : '12%' }}>Sub party</th>
            )}
            <th style={{ padding: '7px 8px', textAlign: 'justify', verticalAlign: 'middle', width: isRegularGst ? '21%' : '26%' }}>Description</th>
            {isRegularGst && <th style={{ padding: '7px 5px', textAlign: 'center', verticalAlign: 'middle', width: '7%' }}>HSN</th>}
            <th style={{ padding: '7px 6px', textAlign: 'center', verticalAlign: 'middle', width: isRegularGst ? '9%' : '11%' }}>Qty</th>
            <th style={{ padding: '7px 10px', textAlign: 'right', verticalAlign: 'middle', width: isRegularGst ? '11%' : '12%' }}>Rate</th>
            <th style={{ padding: '7px 10px', textAlign: 'right', verticalAlign: 'middle', width: isRegularGst ? '8%' : '10%' }}>Disc</th>
            <th style={{ padding: '7px 10px', textAlign: 'right', verticalAlign: 'middle', width: isRegularGst ? '14%' : '24%' }}>Amount</th>
            {isRegularGst && <th style={{ padding: '7px 6px', textAlign: 'center', verticalAlign: 'middle', width: '6%' }}>GST%</th>}
            {isRegularGst && <th style={{ padding: '7px 10px', textAlign: 'right', verticalAlign: 'middle', borderRadius: '0 6px 0 0', width: '11%' }}>Total</th>}
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item, idx) => (
            <tr key={idx} className="invoice-item-row" style={{ 
              background: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <td style={{ padding: itemRowCellPadding, fontWeight: '500', fontSize: '9px', textAlign: 'center', verticalAlign: 'middle' }}>{idx + 1}</td>
              {hasSubPartyColumn && (
                <td style={{ padding: itemRowCellPadding, fontSize: '8px', textAlign: 'left', verticalAlign: 'middle', color: '#334155', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {item._subPartyName ? item._subPartyName : '—'}
                </td>
              )}
              <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'justify', verticalAlign: 'middle', textJustify: 'inter-word' }}>
                <div style={{ fontWeight: '500', wordWrap: 'break-word', overflowWrap: 'anywhere', wordBreak: 'break-word', textAlign: 'justify', textJustify: 'inter-word' }}>{item.description}</div>
                {Array.isArray(item.tags) && item.tags.length > 0 && (
                  <div style={{ fontSize: '8px', color: '#334155', marginTop: '2px', textAlign: 'justify', textJustify: 'inter-word' }}>
                    Tags: {item.tags.join(', ')}
                  </div>
                )}
                {item.qtyDisplay && (
                  <div style={{ fontSize: '8px', color: '#64748b', marginTop: '2px', textAlign: 'justify', textJustify: 'inter-word' }}>
                    {item.qtyDisplay ? item.qtyDisplay : `${item.nos || item.qty || item.Nos || 1}x${item.length || 1}x${item.height || 1}`}
                  </div>
                )}
              </td>
              {isRegularGst && (
                <td style={{ padding: itemRowCellPadding, textAlign: 'center', verticalAlign: 'middle', color: '#64748b', fontSize: '8px' }}>{item.hsn}</td>
              )}
              <td style={{ padding: itemRowCellPadding, textAlign: 'center', verticalAlign: 'middle', color: '#64748b', fontSize: '8px' }}>
                {item.qty} {item.unit || item.quantityMeasurement || 'Nos'}
              </td>
              <td style={{ padding: numericCellPadding, textAlign: 'right', verticalAlign: 'middle', fontWeight: '500', fontSize: '10px', fontVariantNumeric: 'tabular-nums' }}>₹{item.rate}</td>
              <td style={{ padding: numericCellPadding, textAlign: 'right', verticalAlign: 'middle', color: '#64748b', fontSize: '9px', fontVariantNumeric: 'tabular-nums' }}>
                {parseFloat(item.lineDiscountValue) > 0
                  ? (item.lineDiscountType === 'percent'
                      ? `${item.lineDiscountValue}%`
                      : item.lineDiscountValue)
                  : ''}
              </td>
              <td style={{ padding: numericCellPadding, textAlign: 'right', verticalAlign: 'middle', fontWeight: '500', fontSize: '10px', fontVariantNumeric: 'tabular-nums' }}>₹{item.amount}</td>
              {isRegularGst && (
                <td style={{ padding: itemRowCellPadding, textAlign: 'center', verticalAlign: 'middle', color: '#64748b', fontSize: '8px' }}>
                  {item.gst || (parseFloat(item.sgst || 0) + parseFloat(item.cgst || 0) + parseFloat(item.igst || 0))}%
                </td>
              )}
              {isRegularGst && (
                <td style={{ padding: numericCellPadding, textAlign: 'right', verticalAlign: 'middle', fontWeight: '600', color: '#1e293b', fontSize: '10px', fontVariantNumeric: 'tabular-nums' }}>
                  ₹{item.total !== undefined && item.total !== null && item.total !== "" ? item.total : item.amount}
                </td>
              )}
            </tr>
          ))}
          {emptyRowCount > 0 && Array.from({ length: emptyRowCount }).map((_, idx) => (
            <tr
              key={`empty-${idx}`}
              className="invoice-filler-row"
              style={{
                background: '#ffffff',
                borderBottom: '1px solid #edf2f7',
                height: '30px',
              }}
            >
              <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'center', verticalAlign: 'middle' }}>&nbsp;</td>
              {hasSubPartyColumn && <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'left', verticalAlign: 'middle' }}>&nbsp;</td>}
              <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'justify', verticalAlign: 'middle' }}>&nbsp;</td>
              {isRegularGst && <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'center', verticalAlign: 'middle' }}>&nbsp;</td>}
              <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'center', verticalAlign: 'middle' }}>&nbsp;</td>
              <td style={{ padding: numericCellPadding, fontSize: '9px', textAlign: 'right', verticalAlign: 'middle' }}>&nbsp;</td>
              <td style={{ padding: numericCellPadding, fontSize: '9px', textAlign: 'right', verticalAlign: 'middle' }}>&nbsp;</td>
              <td style={{ padding: numericCellPadding, fontSize: '9px', textAlign: 'right', verticalAlign: 'middle' }}>&nbsp;</td>
              {isRegularGst && <td style={{ padding: itemRowCellPadding, fontSize: '9px', textAlign: 'center', verticalAlign: 'middle' }}>&nbsp;</td>}
              {isRegularGst && <td style={{ padding: numericCellPadding, fontSize: '9px', textAlign: 'right', verticalAlign: 'middle' }}>&nbsp;</td>}
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div className="invoice-post-items">
      {/* Bank & Payment Details + Totals */}
      <div className="flex flex-col md:flex-row gap-3 mb-3 bank-totals-flex-row">
        {(companyDetails.bankName || companyDetails.bankAccount || companyDetails.bankIfsc || companyDetails.upiId || showPlaceholders) && (
          <div className="bank-box" style={{ width: '100%', maxWidth: 'calc(46% - 8mm)', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#667eea', marginBottom: '6px' }}>Bank & Payment Details</div>
            {(companyDetails.bankName || showPlaceholders) && (
              <div style={{ fontSize: '16px', marginBottom: '4px', fontWeight: '500' }}>
                {companyDetails.bankName || (showPlaceholders ? '[Bank Name - Add in Company Details]' : '')}
              </div>
            )}
            {(companyDetails.bankAccount || showPlaceholders) && (
              <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '2px' }}>
                A/C: {companyDetails.bankAccount || (showPlaceholders ? '[Account Number - Add in Company Details]' : '')}
              </div>
            )}
            {(companyDetails.bankIfsc || showPlaceholders) && (
              <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '2px' }}>
                IFSC: {companyDetails.bankIfsc || (showPlaceholders ? '[IFSC Code - Add in Company Details]' : '')}
              </div>
            )}
            {(companyDetails.upiId || showPlaceholders) && (
              <div style={{ fontSize: '15px', color: '#64748b', marginBottom: '2px' }}>
                UPI: {companyDetails.upiId || (showPlaceholders ? '[UPI ID - Add in Company Details]' : '')}
              </div>
            )}
            {/* QR Code and Payment Link */}
            <div style={{ marginTop: '8px', textAlign: 'center' }}>
              {createUPIQR(companyDetails.upiQrUrl, screenPreviewBoost ? 'w-28 h-28' : 'w-20 h-20')}
              {companyDetails.paymentGatewayLink && (
                <a href={companyDetails.paymentGatewayLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm" style={{ fontSize: '14px' }}>Pay Now</a>
              )}
              {showPlaceholders && !companyDetails.upiQrUrl && (
                <div className="text-gray-500" style={{ fontSize: '14px' }}>[UPI QR Code - Add in Company Details]</div>
              )}
            </div>
          </div>
        )}
        {companyDetails.gstinType === 'Regular' ? (
          <div className="totals-box gst-totals-table" style={{ width: '100%', maxWidth: '54%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', padding: '4px 2px 4px 8px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '8px' }}>Summary</div>
            <table className="w-full border mb-3 gst-summary-print-table" style={{ fontSize: '14px', tableLayout: 'fixed', width: '100%', maxWidth: '100%' }}>
              <thead>
                <tr className="bg-gray-100">
                  <th className="border" style={{ padding: '8px 10px', fontWeight: '600' }}>GST %</th>
                  <th className="border" style={{ padding: '8px 10px', fontWeight: '600' }}>Taxable</th>
                  <th className="border" style={{ padding: '8px 10px', fontWeight: '600' }}>SGST</th>
                  <th className="border" style={{ padding: '8px 10px', fontWeight: '600' }}>CGST</th>
                  <th className="border" style={{ padding: '8px 10px', fontWeight: '600' }}>IGST</th>
                  <th className="border" style={{ padding: '8px 10px', fontWeight: '600' }}>Total Tax</th>
                </tr>
              </thead>
              <tbody>
                {gstTableRows.map((row, idx) => (
                  <tr key={idx}>
                    <td className="border text-center" style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{previewMode ? blur(row.percent, true) : row.percent}%</td>
                    <td className="border text-right" style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{previewMode ? blur(row.taxable, true) : row.taxable.toFixed(2)}</td>
                    <td className="border text-right" style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{previewMode ? blur(row.sgst, true) : row.sgst.toFixed(2)}</td>
                    <td className="border text-right" style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{previewMode ? blur(row.cgst, true) : row.cgst.toFixed(2)}</td>
                    <td className="border text-right" style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{previewMode ? blur(row.igst, true) : row.igst.toFixed(2)}</td>
                    <td className="border text-right" style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{previewMode ? blur(row.total, true) : row.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {discount > 0 && (
              <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Amount: ₹{previewMode ? blur(grossAmount, true) : grossAmount}</div>
            )}
            {discount > 0 && (
              <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Discount: -₹{previewMode ? blur(discount, true) : discount} {discountPercentLabel}</div>
            )}
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Taxable Amount: ₹{previewMode ? blur(discountedSubtotal, true) : discountedSubtotal}</div>
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>SGST: ₹{previewMode ? blur(totalSGST, true) : totalSGST}</div>
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>CGST: ₹{previewMode ? blur(totalCGST, true) : totalCGST}</div>
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>IGST: ₹{previewMode ? blur(totalIGST, true) : totalIGST}</div>
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Service Charges: ₹{previewMode ? blur(totalVisibleServiceCharges, true) : totalVisibleServiceCharges}</div>
            <div style={{ fontSize: '19px', fontWeight: 'bold', color: '#1e293b', marginTop: '8px', paddingTop: '8px', borderTop: '2px solid #fbbf24', fontVariantNumeric: 'tabular-nums' }}>
              Total: ₹{previewMode ? blur(grandTotal, true) : grandTotal}
            </div>
            <div style={{ fontSize: '15px', marginTop: '6px', color: '#64748b', fontStyle: 'italic', lineHeight: '1.3', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {numToWords(roundTo2Decimals(grandTotal))}
            </div>
          </div>
        ) : (
          <div className="totals-box" style={{ width: '100%', maxWidth: '54%', minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word', padding: '4px 2px 4px 8px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#f59e0b', marginBottom: '8px' }}>Summary</div>
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Amount: ₹{previewMode ? blur(grossAmount, true) : grossAmount}</div>
            {discount > 0 && (
              <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Discount: -₹{previewMode ? blur(discount, true) : discount} {discountPercentLabel}</div>
            )}
            <div style={{ fontSize: '15px', marginBottom: '6px', lineHeight: 1.45, fontVariantNumeric: 'tabular-nums' }}>Service Charges: ₹{previewMode ? blur(totalVisibleServiceCharges, true) : totalVisibleServiceCharges}</div>
            <div style={{ fontSize: '19px', fontWeight: 'bold', color: '#1e293b', marginTop: '8px', paddingTop: '8px', borderTop: '2px solid #fbbf24', fontVariantNumeric: 'tabular-nums' }}>
              Total: ₹{previewMode ? blur(nonGstTotal, true) : nonGstTotal}
            </div>
            <div style={{ fontSize: '15px', marginTop: '6px', color: '#64748b', fontStyle: 'italic', lineHeight: '1.3', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              {numToWords(nonGstTotal)}
            </div>
          </div>
        )}
      </div>

      {invoiceNote && (
        <div style={{ 
          fontSize: '12px', 
          padding: '10px',
          marginBottom: '28px',
          background: '#eff6ff',
          borderLeft: '4px solid #3b82f6',
          borderRadius: '6px',
          color: '#1e3a8a',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px' }}>
            Note:
          </div>
          <div style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {previewMode ? blur(invoiceNote, true) : invoiceNote}
          </div>
        </div>
      )}

      {/* Terms and Conditions */}
      {(companyDetails.salesTerms || companyDetails.terms || showPlaceholders) && (
        <div style={{ 
          fontSize: '12px', 
          padding: '10px',
          marginBottom: '6px',
          background: '#ecfdf5',
          borderLeft: '4px solid #10b981',
          borderRadius: '6px',
          color: '#065f46',
          lineHeight: '1.5'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '13px' }}>
            Terms & Conditions:
          </div>
          <div style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {companyDetails.salesTerms || companyDetails.terms || (showPlaceholders ? '[Sales Terms - Add in Company Details]' : '')}
          </div>
        </div>
      )}





      {/* Disclaimer for Composition Scheme */}
      {companyDetails.gstinType === 'Composition' && (
        <div style={{ 
          fontSize: '12px', 
          color: '#dc2626', 
          fontWeight: 'bold', 
          padding: '6px',
          background: '#fef2f2',
          borderRadius: '4px',
          marginBottom: '6px'
        }}>
          Composition taxable person - Not eligible to collect tax
        </div>
      )}

      {/* Footer: Signatures */}
      <div className="invoice-footer flex justify-between items-end" style={{ minHeight: '88px', marginTop: '16px', paddingTop: '36px' }}>
        <div style={{ textAlign: 'center', fontSize: '12px' }}>
          <div style={{ 
            borderTop: '2px solid #1e293b', 
            width: '100px', 
            margin: '0 auto 6px',
            paddingTop: '4px'
          }}>
            <div style={{ color: '#64748b' }}>Receiver's Signature</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '12px' }}>
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'center', 
            marginBottom: '6px' 
          }}>
            {bankDetails.sealUrl && createSealOrSign(bankDetails.sealUrl, 'Company Seal', 'h-10 w-10')}
            {bankDetails.signUrl && createSealOrSign(bankDetails.signUrl, 'Company Sign', 'h-10 w-20')}
          </div>
          <div style={{ color: '#64748b' }}>Company Sign & Seal</div>
        </div>
      </div>
      </div>
      </div>

             <style>{`
         @media screen {
           /* font-size boost (not transform) — transform draws past the white page box so signatures sit on grey */
           #invoice-template.invoice-screen-preview-boost {
             min-height: auto !important;
             font-size: 100% !important;
           }
           #invoice-template.invoice-screen-preview-boost .invoice-print-inner {
             transform: none !important;
             width: 100% !important;
             max-width: 100% !important;
             margin-left: 0 !important;
             margin-right: 0 !important;
           }
           #invoice-template.invoice-screen-preview-boost tr.invoice-filler-row {
             display: none !important;
           }
         }

         @media print {
           #invoice-template.invoice-screen-preview-boost .invoice-print-inner {
             transform: none !important;
             width: 100% !important;
             max-width: 100% !important;
             margin-left: 0 !important;
             margin-right: 0 !important;
           }
           #invoice-template.invoice-screen-preview-boost tr.invoice-filler-row {
             display: table-row !important;
           }
         }

         #invoice-template {
           box-sizing: border-box !important;
           max-width: 100% !important;
           overflow-x: visible !important;
         }

         /* PDF export: never clip — overflow:hidden was cropping Save as PDF */
         #invoice-template.invoice-pdf-export {
           overflow: visible !important;
           overflow-x: visible !important;
           overflow-y: visible !important;
           height: auto !important;
           max-height: none !important;
         }

         /* Sub party breakdown: long assigned-line text must wrap in preview + PDF */
         #invoice-template .subparty-breakdown-table {
           table-layout: fixed !important;
           width: 100% !important;
         }
         #invoice-template .subparty-breakdown-assigned {
           word-break: break-word !important;
           overflow-wrap: anywhere !important;
           hyphens: auto !important;
           max-width: 0 !important;
         }

         /* Slight horizontal spread for body copy (uses extra width without scaling) */
         #invoice-template .seller-buyer-box {
           text-align: justify !important;
           text-justify: inter-word !important;
         }
         #invoice-template .seller-buyer-box > div:first-child,
         #invoice-template .seller-buyer-box > div:nth-child(2) {
           text-align: left !important;
         }

         /* Ensure seller and buyer are always side by side with proper outlines */
         .seller-buyer-container {
           display: flex !important;
           flex-direction: row !important;
           gap: 12px !important;
           align-items: flex-start !important;
           min-width: 0 !important;
         }
         
         .seller-buyer-box {
           flex: 1 !important;
           border: 1px solid #d1d5db !important;
           border-radius: 6px !important;
           padding: 12px !important;
           background-color: #f9fafb !important;
           min-width: 0 !important;
           overflow-wrap: anywhere !important;
           word-break: break-word !important;
         }
         
         /* Ensure bank details and totals are always side by side */
         .bank-totals-flex-row {
           display: flex !important;
           flex-direction: row !important;
           gap: 12px !important;
           align-items: flex-start !important;
         }
         
         .bank-box {
           flex: 1 !important;
           max-width: 46% !important;
           min-width: 0 !important;
           overflow-wrap: anywhere !important;
           word-break: break-word !important;
         }
         
         .totals-box {
           flex: 1 !important;
           max-width: 54% !important;
           min-width: 0 !important;
           overflow-wrap: anywhere !important;
           word-break: break-word !important;
         }

         .invoice-table-wrap {
           max-width: 100% !important;
         }

         .invoice-items-table {
           max-width: 100% !important;
         }

         .gst-summary-print-table {
           table-layout: fixed !important;
           width: 100% !important;
           max-width: 100% !important;
         }

         .gst-summary-print-table th,
         .gst-summary-print-table td {
           overflow-wrap: anywhere !important;
           word-break: break-word !important;
         }

         .invoice-footer {
           margin-top: 24px !important;
           padding-top: 36px !important;
           min-height: 88px !important;
         }


         /* Mobile preview readability without affecting print layout */
         @media screen and (max-width: 640px) {
           #invoice-template.invoice-screen-preview-boost {
             font-size: 100% !important;
           }

           #invoice-template {
             padding: 10px !important;
           }

           .invoice-header-row {
             flex-direction: column !important;
             align-items: flex-start !important;
           }

           .seller-buyer-container {
             flex-direction: column !important;
             gap: 8px !important;
           }

           .seller-buyer-box {
             width: 100% !important;
             padding: 10px !important;
           }

           .bank-totals-flex-row {
             flex-direction: column !important;
             gap: 8px !important;
           }

           .bank-box,
           .totals-box,
           .gst-totals-table {
             width: 100% !important;
             max-width: 100% !important;
           }

           .invoice-items-table {
             font-size: 9px !important;
           }

           .invoice-footer {
             margin-top: 24px !important;
             padding-top: 28px !important;
             min-height: 80px !important;
             flex-wrap: wrap !important;
             gap: 12px !important;
           }
         }
         
         /* Print-specific styles */
         @media print {
           body {
             -webkit-print-color-adjust: exact;
             print-color-adjust: exact;
           }
           
           @page {
             size: ${pageDims.width}mm ${pageDims.height}mm ${orientation};
             margin: 8mm;
           }
           
           .print\\:p-0 { padding: 0 !important; }
           .print\\:shadow-none { box-shadow: none !important; }
           .print\\:border-0 { border: none !important; }

           ${buildInvoiceCompactLayoutCss('#invoice-template', pageDims)}
         }

         /* html2canvas/html2pdf only apply screen CSS — mirror print layout for Save as PDF */
         @media screen {
           ${buildInvoiceCompactLayoutCss('#invoice-template.invoice-pdf-export', pageDims)}
         }
       `}</style>
    </div>
  );
}

export default InvoiceTemplate; 